import type {
  InsuranceCompanyOption,
  InsuranceQuoteListItem,
  QuoteStatusResponse,
  RequestQuotesRequest,
  RequestQuotesResponse,
  ValidateLicensePlateRequest,
  ValidateLicensePlateResponse,
  ValidateRutRequest,
  ValidateRutResponse,
} from '../types/insuranceQuoteForm';
import baseApi from './baseApi';

const insuranceQuoteFormApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    validateRut: build.mutation<ValidateRutResponse, ValidateRutRequest>({
      query: (body) => ({
        url: '/insurance-quote-form/validate-rut',
        method: 'POST',
        body,
      }),
    }),
    validateLicensePlate: build.mutation<ValidateLicensePlateResponse, ValidateLicensePlateRequest>({
      query: (body) => ({
        url: '/insurance-quote-form/validate-license-plate',
        method: 'POST',
        body,
      }),
    }),
    requestQuotes: build.mutation<RequestQuotesResponse, RequestQuotesRequest>({
      query: (body) => ({
        url: '/insurance-quote-form/request-quotes',
        method: 'POST',
        body,
      }),
    }),
    getQuoteStatus: build.query<QuoteStatusResponse, number>({
      query: (leadId) => ({
        url: `/insurance-quote-form/status/${leadId}`,
        method: 'GET',
      }),
    }),
    getCompanies: build.query<InsuranceCompanyOption[], number>({
      query: (businessUnitId) => ({
        url: `/insurance-quote-form/companies/${businessUnitId}`,
        method: 'GET',
      }),
    }),
    getQuotes: build.query<
      { rows: InsuranceQuoteListItem[]; count: number },
      {
        leadId: number;
        paginationModel?: string;
        filterModel?: string;
        sortModel?: string;
      }
    >({
      query: ({ leadId, paginationModel, filterModel, sortModel }) => ({
        url: `/insurance-quote-form/quotes/${leadId}`,
        method: 'GET',
        params: {
          ...(paginationModel ? { paginationModel } : {}),
          ...(filterModel ? { filterModel } : {}),
          ...(sortModel ? { sortModel } : {}),
        },
      }),
    }),
    downloadQuotesPdf: build.mutation<
      { url: string; fileName: string },
      { leadId: number; filterModel?: string; sortModel?: string; preferredCurrency?: 'UF' | 'CLP' }
    >({
      query: ({ leadId, filterModel, sortModel, preferredCurrency = 'UF' }) => ({
        url: `/insurance-quote-form/quotes/${leadId}/download-pdf`,
        method: 'GET',
        params: {
          ...(filterModel ? { filterModel } : {}),
          ...(sortModel ? { sortModel } : {}),
          preferredCurrency: preferredCurrency ?? 'UF',
        },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useValidateRutMutation,
  useValidateLicensePlateMutation,
  useRequestQuotesMutation,
  useGetQuoteStatusQuery,
  useGetCompaniesQuery,
  useGetQuotesQuery,
  useDownloadQuotesPdfMutation,
} = insuranceQuoteFormApi;
